import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import { getScreeningWindows, requestPlatformAccess } from "../services/TeacherAPIClient";
import {
  assertSubmittedRequestResponse,
  getSupportRequestErrorMessage,
  getSupportRequestReference
} from "../utils/teacherWorkflowRequests";
import "./TeacherRequestEarlyPlatformAccessModal.css";

const accessTypes = [
  {
    value: "Open an upcoming screening window early",
    helper: "Use before the official opening date"
  },
  {
    value: "Request access to a closed screening window",
    helper: "Use when a previous window was missed"
  }
];

const fallbackWindows = [
  { value: "Baseline", helper: "Upcoming window" },
  { value: "Midpoint", helper: "Opens later in the year" },
  { value: "End-of-Year", helper: "Opens later in the year" }
];

const LEGACY_CLOSED_WINDOW_ACCESS_TYPE = "Reopen a closed screening window";
const CLOSED_WINDOW_ACCESS_TYPE = "Request access to a closed screening window";

const normaliseAccessType = (value) =>
  value === LEGACY_CLOSED_WINDOW_ACCESS_TYPE ? CLOSED_WINDOW_ACCESS_TYPE : value;

function TeacherRequestEarlyPlatformAccessModal({
  show,
  handleClose,
  prefill = null,
  schoolid = null,
  teacherName = "",
  teacherEmail = "",
  onViewScreenerGuidance = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [accessType, setAccessType] = useState("");
  const [screeningWindow, setScreeningWindow] = useState("");
  const [requestedOpenDate, setRequestedOpenDate] = useState("");
  const [reopenPeriod, setReopenPeriod] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supportReference, setSupportReference] = useState("");
  const [screeningWindows, setScreeningWindows] = useState(fallbackWindows);

  useEffect(() => {
    if (!show) return;
    const initialAccessType = normaliseAccessType(prefill?.accessType || "");

    setStage("form");
    setAccessType(initialAccessType);
    setScreeningWindow(prefill?.screeningWindow || "");
    setRequestedOpenDate(prefill?.requestedOpenDate || "");
    setReopenPeriod(prefill?.reopenPeriod || "");
    setReason("");
    setError("");
    setIsSubmitting(false);
    setSupportReference("");

    if (schoolid) {
      getScreeningWindows(schoolid)
        .then((response) => {
          const rows = Array.isArray(response) ? response : response?.data || response?.rows || [];
          const nextWindows = rows
            .map((windowRow) => ({
              value: windowRow.name || windowRow.screeningWindow || windowRow.windowName || windowRow.label,
              helper: [windowRow.status, windowRow.reopenEligible ? "reopen eligible" : ""].filter(Boolean).join(" - ")
            }))
            .filter((windowRow) => windowRow.value);

          if (nextWindows.length > 0) {
            setScreeningWindows(nextWindows);
          }
        })
        .catch(() => {
          setScreeningWindows(fallbackWindows);
        });
    }
  }, [show, prefill, schoolid]);

  const isClosedWindow = accessType === CLOSED_WINDOW_ACCESS_TYPE;
  const canContinue =
    accessType &&
    screeningWindow &&
    (isClosedWindow || requestedOpenDate.trim().length > 0);
  const canSubmitAccess =
    accessType &&
    screeningWindow &&
    (isClosedWindow ? reopenPeriod.trim().length > 0 : requestedOpenDate.trim().length > 0);

  const resetForAnotherWindow = () => {
    setStage("form");
    setAccessType("");
    setScreeningWindow("");
    setRequestedOpenDate("");
    setReopenPeriod("");
    setReason("");
    setError("");
    setSupportReference("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: supportReference,
        message: "Platform access request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const sendAccessRequest = async () => {
    if (!canSubmitAccess || isSubmitting) return;
    if (!schoolid) {
      setError("School context is missing. Refresh and try again.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const mapAccessType = (value) => {
      if (value === "Open an upcoming screening window early") return "early_open";
      if (value === "Reopen a closed screening window") return "reopen";
      if (value === "We need a different testing window") return "extension";
      return "other";
    };

    const payload = {
      accessType: mapAccessType(accessType),
      screeningWindow,
      requestedDate: isClosedWindow ? undefined : requestedOpenDate,
      requestedPeriod: isClosedWindow ? reopenPeriod : undefined,
      reason
    };

    try {
      const response = await requestPlatformAccess(schoolid, payload);
      assertSubmittedRequestResponse(response);
      setSupportReference(getSupportRequestReference(response));
      setStage("success");
    } catch (submitError) {
      setError(getSupportRequestErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderOptionCard = ({ value, helper }, selectedValue, setValue, name) => (
    <label key={value} className={`early-access-option${selectedValue === value ? " selected" : ""}`}>
      <Form.Check
        type="radio"
        name={name}
        checked={selectedValue === value}
        onChange={() => setValue(value)}
        aria-label={value}
      />
      <span>
        <strong>{value}</strong>
        <span>{helper}</span>
      </span>
    </label>
  );

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-early-access-modal">
      {stage === "form" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Request Screening Window Access</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="early-access-intro">Choose the access you need.</p>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="early-access-section">
              <Form.Label>What access do you need?</Form.Label>
              <div className="early-access-options">
                {accessTypes.map((option) => renderOptionCard(option, accessType, setAccessType, "earlyAccessType"))}
              </div>
            </Form.Group>

            <Form.Group className="early-access-section">
              <Form.Label>Which screening window?</Form.Label>
              <div className="early-access-options">
                {screeningWindows.map((option) => renderOptionCard(option, screeningWindow, setScreeningWindow, "earlyAccessWindow"))}
              </div>
            </Form.Group>

            {!isClosedWindow && (
              <Form.Group className="early-access-section" controlId="earlyAccessOpenDate">
                <Form.Label>Requested open date</Form.Label>
                <Form.Text as="div" className="early-access-field-label">Date</Form.Text>
                <Form.Control
                  type="date"
                  value={requestedOpenDate}
                  onChange={(event) => setRequestedOpenDate(event.target.value)}
                />
              </Form.Group>
            )}

            <Form.Group className="early-access-section" controlId="earlyAccessReason">
              <Form.Label>Reason (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Please add a short note explaining why you need access"
              />
            </Form.Group>
            <Alert variant="info" className="early-access-warning">
              Your request will be reviewed. Screening window access will not change unless the request is approved.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <span className="early-access-footer-note">{canContinue ? "Request ready." : "Add the requested date or period."}</span>
            <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => isClosedWindow ? setStage("closed") : sendAccessRequest()}
              disabled={!canContinue || isSubmitting}
            >
              {isClosedWindow ? "Next" : "Request access"}
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "closed" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Request Screening Window Access</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="early-access-intro">Use this if children missed an earlier screening window.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="warning" className="early-access-warning">
              <strong>Request {screeningWindow} access</strong>
              <span>Send a request for temporary access to the {screeningWindow} screening window because some children were not screened.</span>
            </Alert>

            <div className="early-access-section">
              <Form.Label as="div">Closed-window access request</Form.Label>
              <Form.Group className="mb-3" controlId="earlyAccessClosedWindow">
                <Form.Label className="early-access-field-label">Closed window</Form.Label>
                <Form.Control value={screeningWindow} readOnly />
              </Form.Group>
              <Form.Group className="mb-3" controlId="earlyAccessReopenPeriod">
                <Form.Label className="early-access-field-label">Requested reopen period</Form.Label>
                <Form.Control value={reopenPeriod} onChange={(event) => setReopenPeriod(event.target.value)} />
              </Form.Group>
              <Form.Group controlId="earlyAccessReopenReason">
                <Form.Label className="early-access-field-label">Reason (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Please add a short note explaining why you need access"
                />
              </Form.Group>
            </div>
            <Alert variant="info" className="early-access-warning">
              Your request will be reviewed. Screening window access will not change unless the request is approved.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <span className="early-access-footer-note">Closed-window request ready.</span>
            <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
            <Button variant="primary" onClick={sendAccessRequest} disabled={!canSubmitAccess || isSubmitting}>
              {isSubmitting ? "Sending..." : "Send request"}
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "success" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Request Sent</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="early-access-intro">ALPACA will review whether the window can be opened and let you know.</p>
            <Alert variant="success" className="early-access-success">
              <span className="early-access-ok-badge" aria-hidden="true"><FaCheckCircle /></span>
              <span>
                <strong>
                  Your screening window access request has been sent for review.
                </strong>
                <span>
                  ALPACA will review whether the window can be opened and let you know.
                </span>
              </span>
            </Alert>
            <div className="early-access-next">
              <strong>What next?</strong>
              <span>{embedded ? "Done returns to SENSAI" : "Done returns to Help Centre home"}. Request another window repeats the action.</span>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="early-access-footer-note">Task complete.</span>
            <Button variant="outline-secondary" onClick={resetForAnotherWindow}>Request another window</Button>
            {onViewScreenerGuidance && (
              <Button
                variant="outline-secondary"
                onClick={() => onViewScreenerGuidance({
                  guidanceQuery: `${screeningWindow} screener video`
                })}
              >
                View screener guidance
              </Button>
            )}
            <Button variant="primary" onClick={handleDone}>Done</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default TeacherRequestEarlyPlatformAccessModal;
